import type { VideoEvidence } from "../../ChatEventTypes";

/** Where a clip sits on its source video's timeline, in source seconds. */
type ClipWindow = {
	videoDuration: number;
	clipStart: number;
	clipDuration: number;
};

const clamp = (value: number, min: number, max: number) =>
	Math.max(min, Math.min(max, value));

export const clipWindow = (clip: VideoEvidence): ClipWindow => {
	const clipDuration = Math.max(
		0,
		clip.clip_duration > 0
			? clip.clip_duration
			: clip.end_time - clip.start_time,
	);
	const videoDuration = Math.max(0, clip.video_duration);
	// Without a source duration there is no wider timeline to place the clip
	// on, so the track is the clip itself.
	if (videoDuration <= 0) {
		return { videoDuration: clipDuration, clipStart: 0, clipDuration };
	}
	return {
		videoDuration,
		clipStart: clamp(
			clip.source_start_time ?? clip.start_time,
			0,
			videoDuration,
		),
		clipDuration: Math.min(clipDuration, videoDuration),
	};
};

export type SeekTarget = {
	/** Seconds from the clip's start, which is what the player seeks by. */
	clipRelativeSeconds: number;
	/** Where that position sits along the full-video track, 0 to 100. */
	trackPercent: number;
};

/**
 * Maps a point along the full-video track to a playback position. The track
 * shows the whole source video, but only the clip's range is playable, so
 * points outside it snap to the clip's start or end.
 */
export const seekTargetAtTrackFraction = (
	clip: VideoEvidence,
	trackFraction: number,
): SeekTarget => {
	const { videoDuration, clipStart, clipDuration } = clipWindow(clip);
	const sourceSeconds = clamp(trackFraction, 0, 1) * videoDuration;
	const clipRelativeSeconds = clamp(sourceSeconds - clipStart, 0, clipDuration);
	return {
		clipRelativeSeconds,
		trackPercent:
			videoDuration > 0
				? ((clipStart + clipRelativeSeconds) / videoDuration) * 100
				: 0,
	};
};
